import { useEffect, useState } from "react";
import { Building2, Search as SearchIcon } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission } from "../../lib/permissions";
import { CUSTOMER_READ_KEYS } from "../../lib/customers";
import { Button } from "../ui/button";
import { ResponsivePopover as Popover, ResponsivePopoverContent as PopoverContent, ResponsivePopoverTrigger as PopoverTrigger } from "../ui/responsive-popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";

const MIN_QUERY = 2;

// D-33: a pharmacy the author already sees — through the prospects (global search, prospects' own scope) or through the customer
// accounts of their portfolio. No new list of pharmacies is exposed; the server checks the choice again (`pharmacy_unknown`).
export function canPickPharmacy(user) {
  return hasPermission(user, "prospects:view") || (!!user?.features?.customers && hasPermission(user, CUSTOMER_READ_KEYS));
}

export default function PharmacyPicker({ value, onChange, testid = "ticket-pharmacy" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const viaProspects = hasPermission(user, "prospects:view");
  const viaCustomers = !!user?.features?.customers && hasPermission(user, CUSTOMER_READ_KEYS);

  useEffect(() => {
    if (!open) return undefined;
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY) { setResults([]); return undefined; }
    setLoading(true);
    const handle = setTimeout(async () => {
      const found = new Map();
      try {
        if (viaProspects) {
          const { data } = await api.get("/search", { params: { q: trimmed } });
          for (const p of data.prospects || []) found.set(p.id, { id: p.id, pharmacy_name: p.pharmacy_name });
        }
        if (viaCustomers) {
          for (const group of ["deploying", "live"]) {
            const { data } = await api.get("/customers", { params: { search: trimmed, group, limit: 10 } });
            for (const c of data.items || []) found.set(c.prospect_id, { id: c.prospect_id, pharmacy_name: c.pharmacy.name });
          }
        }
      } catch (e) {
        // best-effort search-as-you-type: the field simply shows no suggestion
      }
      setResults([...found.values()]);
      setLoading(false);
    }, 300);
    return () => clearTimeout(handle);
  }, [query, open, viaProspects, viaCustomers]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-start font-normal" data-testid={testid}>
          <SearchIcon size={14} className="mr-2 text-slate-400 shrink-0" />
          <span className="truncate">{value ? value.pharmacy_name : "Rechercher une pharmacie…"}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-80" align="start" title="Pharmacie">
        <Command shouldFilter={false}>
          <CommandInput value={query} onValueChange={setQuery} placeholder="Nom de la pharmacie…" data-testid={`${testid}-search`} />
          <CommandList>
            {query.trim().length < MIN_QUERY ? (
              <div className="py-6 text-center text-xs text-slate-400">Tapez au moins {MIN_QUERY} caractères.</div>
            ) : loading ? (
              <div className="py-6 text-center text-xs text-slate-400">Recherche…</div>
            ) : results.length === 0 ? (
              <CommandEmpty>Aucune pharmacie trouvée</CommandEmpty>
            ) : (
              <CommandGroup>
                {results.map((p) => (
                  <CommandItem key={p.id} value={p.id} onSelect={() => { onChange(p); setOpen(false); }} data-testid={`${testid}-option`}>
                    <Building2 size={14} className="mr-2 text-slate-400" />{p.pharmacy_name}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
